import * as React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { Button } from '../Button/Button';
import { Link } from '../Link/Link';
import { Icon } from '../Icon/Icon';
import { FancyList } from '../FancyList/FancyList';
import { Heading } from '../Heading/Heading';
import { EventPageHero } from '../EventPageHero/EventPageHero';
import { Markdown } from '../Markdown/Markdown';
import { AudioEmbed } from '../AudioEmbed/AudioEmbed';
import { SlidesEmbed } from '../SlidesEmbed/SlidesEmbed';
import { VideoEmbed } from '../VideoEmbed/VideoEmbed';
import { CaseStudyText } from '../CaseStudyText/CaseStudyText';

import * as cs from './EventPage.module.css';

const propTypes = {
  pageTitle: PropTypes.string.isRequired,
  intro: PropTypes.string.isRequired,
  coverImage: PropTypes.object,
  bgImages: PropTypes.shape({
    default: PropTypes.object,
    original: PropTypes.shape({
      desktop: PropTypes.object,
      mobile: PropTypes.object,
    }),
  }),
  isBgFullSize: PropTypes.bool,
  whiteBg: PropTypes.bool.isRequired,
  bgColorOverflow: PropTypes.bool,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      to: PropTypes.string,
    }).isRequired,
  ).isRequired,
  opensourceProjects: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      linksExtra: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  ),
  products: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      linksExtra: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  ),
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ),
  snippet: PropTypes.object.isRequired,
  cases: PropTypes.arrayOf(PropTypes.object.isRequired),
  caseStudyPreview: PropTypes.string,
  caseStudySlug: PropTypes.string,
  customCss: PropTypes.string,
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      about: PropTypes.string.isRequired,
      email: PropTypes.string,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  dateStart: PropTypes.string,
  dateStartISO: PropTypes.string,
  dateStartDay: PropTypes.string,
  dateStartMonth: PropTypes.string,
  dateEndDay: PropTypes.string,
  dateEndMonth: PropTypes.string,
  eventType: PropTypes.string.isRequired,
  city: PropTypes.string,
  eventTitle: PropTypes.string,
  url: PropTypes.string,
  moreEvents: PropTypes.arrayOf(PropTypes.object),
  signupUrl: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, url: PropTypes.string }),
  ),
  audios: PropTypes.arrayOf(PropTypes.string),
  video: PropTypes.string,
  live: PropTypes.string,
  slides: PropTypes.string,
  posts: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  rawIntro: PropTypes.string,
  upcoming: PropTypes.bool,
  passed: PropTypes.bool,
};

const EventPage = ({
  snippet,
  pageTitle,
  products,
  opensourceProjects,
  clients,
  intro,
  whiteBg,
  dateStart,
  dateStartISO,
  dateStartDay,
  dateStartMonth,
  dateEndDay,
  dateEndMonth,
  eventType,
  city,
  eventTitle,
  url,
  moreEvents,
  coverImage,
  bgImages,
  isBgFullSize,
  bgColorOverflow,
  tags,
  authors,
  signupUrl,
  links,
  audios,
  video,
  live,
  slides,
  posts,
  rawIntro,
  upcoming,
  // passed,
  customCss,
  cases,
  caseStudyPreview,
  caseStudySlug,
}: any) => {
  const allCaseEntries =
    cases?.map((entry: any) => ({
      ...entry,
      entryType: 'post',
    })) || [];

  const allCaseSlugs = (cases || []).map((entry: any) => entry.slug);

  const caseEntries = allCaseEntries.filter(
    (entry: any) => entry.slug !== caseStudySlug,
  );

  const moreWorkEntries = [
    ...[...products, ...opensourceProjects]
      .map((entry) => ({
        ...entry,
        entryType: 'opensourceProject',
      }))
      .slice(0, 10),
    ...posts
      .filter((entry: any) => !allCaseSlugs.includes(entry.slug))
      .map((entry: any) => ({
        ...entry,
        entryType: 'post',
      })),
    ...clients
      .map((entry: any) => ({
        ...entry,
        entryType: 'client',
      }))
      .slice(0, 10),
  ];

  const withAttend = upcoming && (signupUrl || url);

  return (
    <>
      {customCss && <style>{customCss}</style>}

      <EventPageHero
        title={pageTitle}
        tags={tags}
        coverImage={coverImage}
        bgImages={bgImages}
        isBgFullSize={isBgFullSize}
        bgColorOverflow={bgColorOverflow}
        whiteBg={whiteBg}
        description={rawIntro}
        snippet={snippet}
        authors={authors}
        dateStart={dateStart}
        dateStartISO={dateStartISO}
        dateStartDay={dateStartDay}
        dateStartMonth={dateStartMonth}
        dateEndDay={dateEndDay}
        dateEndMonth={dateEndMonth}
        eventType={eventType}
        city={city}
        eventTitle={eventTitle}
        url={url}
      />

      {intro && <Markdown body={intro} data-theme-reset className={cs.intro} />}
      {caseStudyPreview && (
        <CaseStudyText
          caseStudyPreview={caseStudyPreview}
          className={cx(cs.caseStudy, { [cs.withAttend]: withAttend })}
          slug={caseStudySlug}
          readMoreText={snippet.readMore}
          data-theme-reset
        />
      )}
      {withAttend && (
        <Button
          to={signupUrl || url}
          variant="solid"
          theme="primary"
          size="lg"
          target="_blank"
          trackEvent="event-signup"
          trackLabel={signupUrl || url}
        >
          {snippet.attend}
        </Button>
      )}

      <section data-theme-reset>
        {Boolean(video || live) && (
          <div className={cs.media}>
            <div className={cs.mediaHeader}>
              {snippet.video}

              <Icon name="video" size={24} className={cs.mediaIcon} />
            </div>

            <VideoEmbed url={video || live} />
          </div>
        )}
        {Boolean(slides) && (
          <div className={cs.media}>
            <div className={cs.mediaHeader}>
              {snippet.slides}

              <Icon name="slides" size={24} className={cs.mediaIcon} />
            </div>

            <SlidesEmbed url={slides} />
          </div>
        )}
        {Boolean(audios && audios.length > 0) && (
          <div className={cs.media}>
            <div className={cs.mediaHeader}>
              {snippet.audio}

              <Icon name="audio" size={24} className={cs.mediaIcon} />
            </div>

            {audios.slice(0, 3).map((audio: any) => (
              <AudioEmbed key={audio} url={audio} />
            ))}
          </div>
        )}
      </section>
      {Boolean(links && links.length > 0) && (
        <section className={cs.links} data-theme-reset>
          <div className={cs.linksHeader}>{snippet.links}</div>

          <div className={cs.linksDescription}>{snippet.linksDescription}</div>

          <ul className={cs.linksList}>
            {links.map((link: any) => (
              <li key={link.url}>
                <Link to={link.url} target="_blank">
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {caseEntries && caseEntries.length > 0 && (
        <section className={cs.moreWork}>
          <Heading level={2}>{snippet.furtherReading}</Heading>

          <FancyList
            entries={caseEntries}
            trackEvent="recommendations-further-reading-event"
          />
        </section>
      )}
      {moreWorkEntries && moreWorkEntries.length > 0 && (
        <section className={cs.moreWork} data-theme-reset>
          <Heading level={2}>{snippet.moreWork}</Heading>

          <FancyList
            entries={moreWorkEntries}
            trackEvent="recommendations-same-orbit-event"
          />
        </section>
      )}
      {moreEvents && moreEvents.length > 0 && (
        <section className={cs.moreEvents} data-theme-reset>
          <Heading level={2}>{snippet.moreEvents}</Heading>

          <FancyList
            entries={moreEvents.map((project: any) => ({
              ...project.node,
              entryType: 'event',
            }))}
            withEntityType={false}
            trackEvent="recommendations-more-events"
          />
        </section>
      )}
    </>
  );
};

EventPage.propTypes = propTypes;

export { EventPage };
